import React, { useMemo } from "react";
import { Tag } from "@jem-open/jem-ui";
import { PreviewParams } from "./types";
import PreviewHeader from "./PreviewHeader";
import SummaryTile from "./SummaryTile";
import { Td, Th } from "./tableHelpers";
import { TimesheetPreviewData } from "../../services/model/ReportPreviewModels";

interface TimesheetPreviewProps {
  params: PreviewParams;
  data: TimesheetPreviewData;
  onDownload: () => void;
}

function statusTagVariant(
  status: string
): "success" | "error" | "drafted" | "pending" {
  if (status === "Complete") return "success";
  if (status === "No Show") return "error";
  if (status === "Replacement") return "pending";
  return "drafted";
}

const fmtDate = (iso: string): string => {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const TimesheetPreview = ({
  params,
  data,
  onDownload,
}: TimesheetPreviewProps): JSX.Element => {
  const displayRows = useMemo(() => data.rows.slice(0, 200), [data.rows]);

  return (
    <>
      <PreviewHeader
        title="Timesheet"
        subtitle="Payroll export"
        params={params}
        onDownload={onDownload}
      />

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-sm mb-lg">
        <SummaryTile
          label="Shifts"
          value={String(data.total_shifts)}
          unit="rostered"
        />
        <SummaryTile
          label="Completion"
          value={`${Math.round(data.completion_pct)}%`}
          unit="complete"
        />
        <SummaryTile
          label="Scheduled"
          value={`${Math.round(data.total_scheduled_hours)}h`}
          unit="hours"
        />
        <SummaryTile
          label="Actual"
          value={`${data.total_actual_hours.toFixed(1)}h`}
          unit="hours confirmed"
        />
        <SummaryTile
          label="Estimated"
          value={`${(
            data.total_estimated_hours ?? data.total_actual_hours
          ).toFixed(1)}h`}
          unit="hours est."
        />
      </div>

      <h2 className="text-sm font-semibold text-greyscale-text-title mb-xs">
        Shift detail
      </h2>
      <p className="text-xs text-greyscale-text-caption mb-sm">
        In the exported file, each site appears on its own sheet plus a summary
        sheet. This preview shows up to {displayRows.length} rows
        {data.rows.length > displayRows.length
          ? ` (of ${data.rows.length} total).`
          : "."}
      </p>
      <div className="overflow-x-auto rounded-lg border border-greyscale-border">
        <table className="w-full text-xs sm:text-sm">
          <thead className="bg-neutral-50 border-b border-greyscale-border">
            <tr>
              <Th>Employee</Th>
              <Th>Emp #</Th>
              <Th>Date</Th>
              <Th>Shift</Th>
              <Th>Scheduled</Th>
              <Th>Actual</Th>
              <Th align="right">Hours</Th>
              <Th align="right">Est.</Th>
              <Th>Source</Th>
              <Th>Status</Th>
            </tr>
          </thead>
          <tbody>
            {displayRows.length === 0 ? (
              <tr>
                <td
                  colSpan={10}
                  className="px-sm py-md text-center text-sm text-greyscale-text-caption"
                >
                  No shifts for the selected range.
                </td>
              </tr>
            ) : (
              displayRows.map((r) => (
                <tr
                  key={`${r.employee_number ?? r.employee_name}-${
                    r.record_date
                  }-${r.shift_label}-${r.location_name}-${r.scheduled}`}
                  className="border-b border-greyscale-border last:border-0"
                >
                  <Td className="font-semibold">{r.employee_name}</Td>
                  <Td mono>{r.employee_number ?? "—"}</Td>
                  <Td>{fmtDate(r.record_date)}</Td>
                  <Td>{r.shift_label}</Td>
                  <Td mono>{r.scheduled}</Td>
                  <Td mono>{r.actual}</Td>
                  <Td align="right" mono>
                    {r.hours.toFixed(2)}
                  </Td>
                  <Td align="right" mono>
                    {r.estimated_hours != null && r.estimated_hours > r.hours
                      ? r.estimated_hours.toFixed(2)
                      : "—"}
                  </Td>
                  <Td>{r.source}</Td>
                  <Td>
                    <Tag variant={statusTagVariant(r.status_label)} size="sm">
                      {r.status_label}
                    </Tag>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default TimesheetPreview;
